import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { find, reduce, isArray, isNil, isEmpty } from 'lodash'
import {
  Expander, ExpanderHeader, ExpanderTrigger, ExpanderContent,
  Tabs, TabsList, TabsTrigger, TabsContent
} from '@ideon-api/ideon-ui'
import PlanInformationComponent from './PlanInformationComponent'
import PlanBenefitsComponent from './PlanBenefitsComponent'
import PlanMemberRatesComponent from './PlanMemberRatesComponent'
import PlanRateComponent from './PlanRateComponent'
import PlanIdentifiersComponent from './PlanIdentifiersComponent'
import medicalFieldGroups from '../../Medical/fields'
import dentalFieldGroups from '../../Dental/dentalFields'
import visionFieldGroups from '../../Vision/visionFields'
import lifeFieldGroups from '../../Life/lifeFields'
import disabilityFieldGroups from '../../Disability/disabilityFields'
import accidentFieldGroups from '../../Accident/accidentFields'
import hospitalIndemnityFieldGroups from '../../HospitalIndemnity/hospitalIndemnityFields'
import criticalIllnessFieldGroups from '../../CriticalIllness/criticalIllnessFields'

export const INFORMATION_TAB = 'Information'
export const BENEFITS_TAB = 'Benefits'
export const RATES_TAB = 'Rates'
export const IDENTIFIERS_TAB = 'Identifiers'

// PD-30615 MR3: the single source of truth for which tabs a plan card has.
// Both the card itself and the list's "Expand all" menu (PlansComponent) read
// this, so the two can't drift apart.
export function tabsFor(displayBenefitsTab) {
  return [INFORMATION_TAB, ...(displayBenefitsTab ? [BENEFITS_TAB] : []), RATES_TAB, IDENTIFIERS_TAB]
}

const COLUMN_CLASS = 'w-28 text-sm text-right'

export class PlanComponent extends Component {
  static propTypes = {
    plan: PropTypes.object.isRequired,
    census: PropTypes.object.isRequired,
    plansByFamily: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.array
    ]),
    displayMonthlyPremium: PropTypes.bool,
    displayContractCode: PropTypes.bool,
    displayBenefitsTab: PropTypes.bool,
    usePlanRateComponent: PropTypes.bool,
    // PD-30615 MR3: lifted to PlansComponent so "Expand all -> <tab>" can
    // drive every card's open state and active tab from one place.
    open: PropTypes.bool.isRequired,
    onOpenChange: PropTypes.func.isRequired,
    tab: PropTypes.string.isRequired,
    onTabChange: PropTypes.func.isRequired
  }

  get censusFamilyPlansPremiums() {
    const { plan, census, plansByFamily } = this.props
    if (isArray(plansByFamily)) {
      const rate = find(plansByFamily, (p) => p.plan_id === plan.id)
      const premium = !isNil(rate) && !isNil(rate.total_premium)
        ? rate.total_premium
        : this.extractPremiumTotal(rate)
      return [{ premium: parseFloat(premium), rate_id: (!isNil(rate) ? rate.id : null) }]
    }
    return census.families.map((f) => {
      const plans = plansByFamily[f.id]
      const familyPlan = find(plans, (p) => p.id === plan.id)
      const premium = familyPlan ? familyPlan.premium : 0
      return { name: f.name, premium }
    })
  }

  get contractCodes() {
    return this.props.plan.identifiers.filter((id) => id.type === 'contract_code')
  }

  get contractCodesString() {
    return this.contractCodes.map((id) => id.value).join(',')
  }

  get fieldGroupsForType() {
    switch (this.props.plan.product_line) {
    case 'medical':
      return medicalFieldGroups
    case 'dental':
      return dentalFieldGroups
    case 'vision':
      return visionFieldGroups
    case 'life':
      return lifeFieldGroups
    case 'disability':
      return disabilityFieldGroups
    case 'accident':
      return accidentFieldGroups
    case 'hospital_indemnity':
      return hospitalIndemnityFieldGroups
    case 'critical_illness':
      return criticalIllnessFieldGroups
    default:
      return null
    }
  }

  get planPremiums() {
    if (this._planPremiums) {
      return this._planPremiums
    }

    this._planPremiums = this.censusFamilyPlansPremiums

    return this._planPremiums
  }

  get totalString() {
    const total = reduce(this.planPremiums, (sum, p) => sum + p.premium, 0)
    return total !== 0 ? `$${total.toFixed(2)}` : 'n/a'
  }

  renderInformationPanel = () => (
    <PlanInformationComponent plan={this.props.plan} fieldGroups={this.fieldGroupsForType} />
  )

  renderBenefitsPanel = () => (
    <PlanBenefitsComponent plan={this.props.plan} fieldGroups={this.fieldGroupsForType} />
  )

  renderRatesPanel = () => {
    const { usePlanRateComponent } = this.props

    if (usePlanRateComponent) return <PlanRateComponent plan={this.props.plan} />

    return (
      <PlanMemberRatesComponent
        planPremiumsByFamily={this.planPremiums}
        plan={this.props.plan}
        census={this.props.census}
      />
    )
  }

  renderIdentifiersPanel = () => (
    <PlanIdentifiersComponent identifiers={this.props.plan.identifiers} />
  )

  panelForTab = (tab) => {
    switch (tab) {
    case BENEFITS_TAB:
      return this.renderBenefitsPanel()
    case RATES_TAB:
      return this.renderRatesPanel()
    case IDENTIFIERS_TAB:
      return this.renderIdentifiersPanel()
    default:
      return this.renderInformationPanel()
    }
  }

  extractPremiumTotal(rate) {
    if (isNil(rate) || isNil(rate.premiums)) return 0
    const tier = Object.values(rate.premiums).find((t) => !isEmpty(t) && t.total)
    return tier ? tier.total : 0
  }

  render() {
    const {
      plan, open, onOpenChange, tab, onTabChange, displayMonthlyPremium, displayContractCode, displayBenefitsTab
    } = this.props

    return (
      <Expander open={open} onOpenChange={onOpenChange} data-testid={`plan-${plan.id}`}>
        {/* ADR 0011: id/premium/codes are siblings of the trigger, never its
          * children -- a fixed-width column needing its own interactive
          * element can't nest inside a role="button" trigger (0.9.0 CHANGELOG). */}
        <ExpanderHeader className="flex items-center gap-4">
          <ExpanderTrigger className="flex-1 text-left font-medium">{plan.name}</ExpanderTrigger>
          <div className={COLUMN_CLASS}>{plan.id}</div>
          {displayMonthlyPremium && <div className={COLUMN_CLASS}>{this.totalString}</div>}
          {displayContractCode && <div className={COLUMN_CLASS}>{this.contractCodesString}</div>}
        </ExpanderHeader>

        <ExpanderContent>
          <Tabs value={tab} onValueChange={onTabChange}>
            <TabsList>
              {tabsFor(displayBenefitsTab).map((tabName) => (
                <TabsTrigger key={tabName} value={tabName}>{tabName}</TabsTrigger>
              ))}
            </TabsList>

            {tabsFor(displayBenefitsTab).map((tabName) => (
              <TabsContent key={tabName} value={tabName}>{this.panelForTab(tabName)}</TabsContent>
            ))}
          </Tabs>
        </ExpanderContent>
      </Expander>
    )
  }
}

export default PlanComponent
